import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { isClient } from "utilities/userType";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import {
  resolveClientLandingPath,
  isDashboardAllowed,
  LANDING_REGISTRATION,
} from "utilities/clientLanding";
import PageLoader from "../Loaders/PageLoader";

/**
 * Keep a merchant out of the dashboard until their application is submitted.
 *
 * SOUND-726 (CPO, 2026-08-28): "when the application is new or in progress, I
 * shouldn't be able to hit the dashboard until it's submitted." The dashboard
 * reports on a live account; an application still being filled in has nothing
 * to report, so it reads as an empty, broken product rather than a next step.
 *
 * This is the mirror of the redirect in useRegistration, and the two point AT
 * each other — /registration sends finished applications here, this sends
 * unfinished ones back. That makes any disagreement between them an infinite
 * loop rather than a wrong answer, which is why BOTH derive from
 * utilities/clientLanding.js and neither carries its own list of statuses.
 *
 * Three cases this must not break, each of which would be a lockout:
 *
 *   - NOT A CLIENT. Internal, partner and processor users have no application
 *     of their own; gating them on one would bounce them off /dashboard
 *     forever. They pass straight through.
 *   - NO APPLICATION AT ALL (404). A parent company has none. Sending them to
 *     the wizard would loop, because useRegistration answers a 404 for a parent
 *     by sending them back here. Treated as "allowed".
 *   - THE LOOKUP FAILED. A 500, a network drop, an expired token. Failing
 *     closed here would lock a legitimate merchant out of their own dashboard
 *     over a transient error, so this fails OPEN — the dashboard is a read-only
 *     surface and the API scopes every request anyway.
 */
export function RequireSubmittedApplication({ children }) {
  const { user } = useAuth();
  // "checking" | "allowed" | { redirectTo }
  const [gate, setGate] = useState("checking");

  useEffect(() => {
    // Auth still resolving. RequireRole/requireAuth own that state; do nothing
    // until we know who this is.
    if (user === null) return undefined;

    // Signed out, or not a merchant — nothing to gate on.
    if (!user || !isClient(user) || !user.companyId) {
      setGate("allowed");
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getApplication(user.companyId),
          user,
          user.companyId,
          null
        );
        if (cancelled) return;

        const status = res?.data?.status;
        if (isDashboardAllowed(status)) {
          setGate("allowed");
        } else {
          setGate({ redirectTo: resolveClientLandingPath(status) });
        }
      } catch (error) {
        if (cancelled) return;

        // No application: a parent company, which belongs on the dashboard and
        // has no wizard to be sent to.
        if (error?.response?.status === 404) {
          setGate("allowed");
          return;
        }

        // Anything else is our problem, not theirs. Let them through.
        console.error("[RequireSubmittedApplication] status lookup failed", error);
        setGate("allowed");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (user === null || gate === "checking") return <PageLoader />;
  if (gate !== "allowed") {
    return <Navigate to={gate.redirectTo || LANDING_REGISTRATION} replace />;
  }
  return children;
}

export default RequireSubmittedApplication;
